// AuthBootstrapProvider — checks backend session cookie validity at startup.
//
// Production: calls GET /api/auth/me. If 401, redirects to /login?next=<path>
// (the login UI page, not the BFF endpoint directly), preserving the
// originally-requested URL so the user lands back where they intended after
// authenticating.
//
// Auth pages (/login, /logout, /logged-out, /session-expired) bypass the check
// entirely — they must render regardless of session state, otherwise a 401 on
// /login itself causes an infinite redirect loop.
//
// Local dev: skips the session check. Dev token flow handled by CapabilitiesProvider.

import { useEffect, useRef, type ReactNode } from 'react';
import { ApiError } from '@/shared/api';
import { useCurrentUserQuery } from '@/entities/user';
import { recordSessionEvent } from '@/shared/auth/sessionAudit';
import { appUrl, relativeAppPath } from '@/shared/config';

const IS_LOCAL = import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'local';

// Pages that must render without a valid session.
const AUTH_PATHS = ['/login', '/logout', '/logged-out', '/session-expired'];

function isAuthPage(): boolean {
  const path = relativeAppPath(window.location.pathname);
  return AUTH_PATHS.some((p) => path === p || path.startsWith(p + '/'));
}

function SessionGuard({ children }: { children: ReactNode }) {
  const bypass = IS_LOCAL || isAuthPage();
  const currentUser = useCurrentUserQuery(!bypass);
  const loginRecorded = useRef(false);
  useEffect(() => {
    if (bypass || currentUser.data == null || loginRecorded.current) return;
    loginRecorded.current = true;
    void recordSessionEvent('login').catch(() => {});
  }, [bypass, currentUser.data]);

  useEffect(() => {
    if (bypass || !(currentUser.error instanceof ApiError)) return;
    if (currentUser.error.context?.status === 401) {
      const next = window.location.pathname + window.location.search + window.location.hash;
      window.location.replace(appUrl(`/login?next=${encodeURIComponent(next)}`));
    }
  }, [bypass, currentUser.error]);

  if (!bypass && currentUser.isLoading) return null;

  return <>{children}</>;
}

export function AuthBootstrapProvider({ children }: { children: ReactNode }) {
  return <SessionGuard>{children}</SessionGuard>;
}
